import { queryClient } from '@/graphql/client';
import { DELETE_SYSTEM_NODES_BY_PARENT_AND_NODE_IDS } from '@/graphql/mutations';
import { request } from '@/graphql/request';
import { useDataManagementPageStore } from '@/stores/dataManagementStore';
import { toast } from '@skand/ui';
import { useMutation } from '@tanstack/react-query';
import { FoldersPageTableData } from '../useFoldersPageColumns';
import { DownloadMultipleFiles } from './DownloadMultipleFiles';
import MoveMultipleFiles from './MoveMultipleFiles';

interface BatchActionFloatingToolbarProps {
  selectedRows: FoldersPageTableData[];
}

export const BatchActionFloatingToolbar = ({ selectedRows }: BatchActionFloatingToolbarProps) => {
  const parentNodeId = useDataManagementPageStore(state => state.parentNodeId);

  const deleteSystemNodesByParentAndNodeIds = useMutation({
    mutationFn: () =>
      request(DELETE_SYSTEM_NODES_BY_PARENT_AND_NODE_IDS, {
        parentNodeId: parentNodeId ?? null,
        systemNodeIds: selectedRows.map(row => row.id),
      }),
    onSuccess: data => {
      queryClient.invalidateQueries(['LIST_SYSTEM_NODES_BY_PARENT_NODE_ID', parentNodeId || null]);

      const nonDeletable =
        data.deleteSystemNodesByParentAndNodeIds?.nonDeletableSystemNodeIdsWithErrorDetails ?? [];
      if (nonDeletable.length !== 0) {
        toast({
          clickToDismiss: true,
          message: (
            <>
              <div className="typo-text-s-em">Not all items were deleted!</div>
              <div>{selectedRows.length - nonDeletable.length} item(s) were deleted.</div>
              <div>
                {nonDeletable.length} item(s) were not deleted because they have children or they
                are used in one or more projects.
              </div>
            </>
          ),
          type: 'info',
        });
      } else {
        toast({
          clickToDismiss: true,
          lifespan: 3000,
          message: `Deleted selected ${selectedRows.length} item(s)!`,
          type: 'success',
        });
      }
    },
  });

  if (selectedRows.length === 0) return null;

  return (
    <div className="fixed bottom-14 left-50% z-1 h-[44px] flex translate-x--50% items-center gap-4 border-1 border-neutral-400 rounded-md border-solid bg-white px-4 py-2 shadow-md">
      <p className="color-neutral-500 typo-text-s">
        {selectedRows.length} selected file{selectedRows.length > 1 ? 's' : ''}
      </p>

      <div className="h-full b-r-neutral-400 b-r-solid" />

      <MoveMultipleFiles selectedRows={selectedRows} />
      <DownloadMultipleFiles selectedRows={selectedRows} />

      <div
        className="i-skand-trashbin cursor-pointer text-5 color-neutral-600 aria-disabled:pointer-events-none aria-disabled:opacity-50"
        role="button"
        onClick={() => deleteSystemNodesByParentAndNodeIds.mutate()}
      />
    </div>
  );
};
